/**
 * Limiting and uploading the files a user attaches to a chat message (the
 * picker, drag-and-drop or a paste).
 */

/** ~8 MB raw per file, matching the backend's per-attachment cap. */
export const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;
export const MAX_ATTACHMENTS = 5;

/** An attachment already uploaded to the fileshare bucket, ready to send in `AgentChatInput` (MEIS-13516, P1). */
export type UploadedAgentAttachment = {
  name: string;
  mediaType: string;
  uploadKey: string;
};

/**
 * Uploads one attachment directly to the fileshare bucket via a presigned
 * PUT (`requestAgentAttachmentUploadUrl` — see `AgentChatBox.graphql`); the
 * backend reads the object back by `uploadKey` once the turn actually runs.
 * Replaces the former inline base64 attachment now that the chat turn
 * travels over GraphQL, whose request body is not a good fit for embedded
 * file bytes.
 */
export async function uploadAgentAttachment(
  file: File,
  requestUploadUrl: (input: {
    fileName: string;
    mediaType: string;
    fileSizeBytes: number;
  }) => Promise<{ uploadUrl: string; uploadKey: string }>
): Promise<UploadedAgentAttachment> {
  const mediaType = file.type || "application/octet-stream";
  const { uploadUrl, uploadKey } = await requestUploadUrl({
    fileName: file.name,
    mediaType,
    fileSizeBytes: file.size,
  });
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "content-type": mediaType },
    body: file,
  });
  if (!response.ok) {
    throw new Error(
      `Could not upload the file ${file.name} (${response.status}).`
    );
  }
  return { name: file.name, mediaType, uploadKey };
}

/**
 * A screenshot pasted from the clipboard arrives as "image.png"; several of
 * them would be indistinguishable in the chat and in the history.
 */
export function pastedAgentFileName(file: File, now: Date = new Date()): File {
  if (!/^image\.(png|jpe?g|gif|webp)$/i.test(file.name)) return file;
  const stamp = now
    .toLocaleTimeString("de-DE", { hour12: false })
    .replace(/:/g, ".");
  const extension = file.name.split(".").pop() ?? "png";
  return new File([file], `Screenshot ${stamp}.${extension}`, {
    type: file.type,
  });
}
